/**
 * 统一请求工具 - 封装 axios，提供统一的错误处理、token 管理、loading 状态
 */

import axios, { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { message } from 'antd'
import { getToken, clearAuth, refreshTokenExpire } from './auth'
import { logOperation } from './operationLog'

// 从环境变量获取 API 地址
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.banchengserver.cn/api'
export const UPLOAD_URL = process.env.REACT_APP_UPLOAD_URL || `${API_BASE_URL}/upload/image`

// 静态资源（/uploads 上传文件）基础地址。
// 生产环境 API_BASE_URL 是相对路径 "/api"，而 /uploads 是后端静态文件目录，
// 走 Nginx 的 /api/ 反代会 404，必须使用 api 域名直接访问。
export const ASSETS_BASE_URL =
  process.env.REACT_APP_ASSETS_BASE_URL ||
  (API_BASE_URL.startsWith('http') ? API_BASE_URL.replace(/\/api\/?$/, '') : 'https://api.banchengserver.cn')

// 请求超时时间
const REQUEST_TIMEOUT = 30000

// 创建 axios 实例（类型为 any，因为响应拦截器返回 response.data 而非 AxiosResponse）
// 注意：不要在这里全局设置 Content-Type: application/json，否则 FormData 上传请求
// 会带上错误的 Content-Type（缺少 boundary），导致后端无法解析文件。
// JSON 请求的 Content-Type 由下方请求拦截器按需设置。
const request: any = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
})

// 白名单路径（不需要 token 的请求）
const WHITE_LIST = ['/auth/admin/login']

// 请求拦截器：添加 token
request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken()

    // 检查是否在白名单
    const url = config.url || ''
    const isWhiteList = WHITE_LIST.some(path => url.includes(path))

    if (token && !isWhiteList) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // 根据请求数据类型自动设置合适的 Content-Type
    const data = (config as any).data
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
      // FormData 上传请求：完全删除 Content-Type，让浏览器自动设置（含正确的 boundary）
      delete (config.headers as any)['Content-Type']
    } else if (
      data !== undefined &&
      data !== null &&
      !(data instanceof URLSearchParams) &&
      typeof data !== 'string'
    ) {
      // 非 FormData 的对象请求，统一用 JSON
      ;(config.headers as any)['Content-Type'] = 'application/json'
    }

    // 记录请求开始时间（用于性能监控）
    ;(config as any)._startTime = Date.now()

    return config
  },
  (error: any) => {
    return Promise.reject(error)
  }
)

// 响应拦截器：统一处理响应和错误
request.interceptors.response.use(
  (response: AxiosResponse) => {
    const res = response.data as any
    const config = response.config as any

    // 计算请求耗时
    const duration = Date.now() - (config._startTime || Date.now())

    // 业务错误处理
    if (res.code !== 200 && res.code !== 0) {
      message.error(res.message || '请求失败')
      return Promise.reject(res)
    }

    // 有 token 的请求，续期 token 过期时间（每次有效请求续期）
    if (getToken()) {
      refreshTokenExpire(parseInt(process.env.REACT_APP_TOKEN_EXPIRE_HOURS || '24', 10))
    }

    // 记录操作日志（仅对有副作用的操作记录）
    const method = config.method?.toUpperCase()
    const url = config.url || ''
    if (method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) && !url.includes('/logs') && !url.includes('/auth/admin/login')) {
      logOperation({
        module: extractModule(url),
        action: extractAction(method, url),
        url,
        method,
        duration,
        status: res.code,
        targetName: '',
        detail: res.message || '',
      }).catch(() => {
        // 日志记录失败不影响主流程
      })
    }

    return res
  },
  (error: any) => {
    if (error.response) {
      const { status, data } = error.response

      // 401 未授权：清除 token 并跳转到登录页
      if (status === 401) {
        // 操作日志接口的 401 不触发跳转，避免刷新循环
        if (error.config?.url && error.config.url.includes('/settings/logs')) {
          console.warn('[Log] 401 ignored for /settings/logs')
          return Promise.reject(error)
        }
        clearAuth()
        message.error('登录已过期，请重新登录')
        // 使用 replace 避免添加历史记录
        window.location.replace('/login')
      } else if (status === 403) {
        message.error('没有权限执行此操作')
      } else if (status === 404) {
        message.error('请求的资源不存在')
      } else if (status >= 500) {
        message.error('服务器错误，请稍后重试')
      } else {
        message.error(data?.message || '请求失败')
      }
    } else if (error.code === 'ECONNABORTED') {
      message.error('请求超时，请检查网络连接')
    } else if (error.message?.includes('Network Error')) {
      message.error('网络错误，请检查连接')
    } else {
      message.error(error.message || '请求失败')
    }

    return Promise.reject(error)
  }
)

// 从 URL 中提取模块名
const extractModule = (url: string): string => {
  const path = url.replace(/^\/+|\/+$/g, '')
  const parts = path.split('/')
  if (parts.length === 0) return 'system'

  const moduleMap: Record<string, string> = {
    products: '商品管理',
    'product-certs': '商品证件',
    price: '价格日期',
    orders: '订单管理',
    members: '会员管理',
    stores: '门店管理',
    coupons: '优惠券管理',
    statistics: '数据报表',
    dashboard: '仪表盘',
    settings: '系统设置',
    upload: '文件上传',
    auth: '认证管理',
    categories: '分类管理',
  }

  return moduleMap[parts[0]] || '系统操作'
}

// 从 URL 和方法中提取操作名
const extractAction = (method: string, url: string): string => {
  const actionMap: Record<string, string> = {
    POST: '新增',
    PUT: '修改',
    PATCH: '更新',
    DELETE: '删除',
  }

  // 特殊操作判断
  if (url.includes('/login')) return '登录'
  if (url.includes('/logout')) return '登出'
  if (url.includes('/export')) return '导出'
  if (url.includes('/import')) return '导入'
  if (url.includes('/batch-delete')) return '批量删除'
  if (url.includes('/batch-save')) return '批量保存'
  if (url.includes('/status')) return '状态更新'
  if (url.includes('/cancel')) return '取消'
  if (url.includes('/verify')) return '审核'
  if (url.includes('/deliver')) return '发货'
  if (url.includes('/grant')) return '发放'
  if (url.includes('/reset')) return '重置'
  if (url.includes('/password')) return '修改密码'

  return actionMap[method] || '操作'
}

// 导出常用方法
export default request

// 便捷方法
export const httpGet = <T = any>(url: string, params?: any, config?: AxiosRequestConfig): Promise<T> => {
  return request.get(url, { params, ...config }) as any
}

export const httpPost = <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
  return request.post(url, data, config) as any
}

export const httpPut = <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
  return request.put(url, data, config) as any
}

export const httpPatch = <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
  return request.patch(url, data, config) as any
}

export const httpDelete = <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  return request.delete(url, config) as any
}
